// frontend/src/pages/UniversalRiskAuditResults.jsx
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";

const STORAGE_KEY = "aifine_public_audit_results";

/* ---------------------------------------------
   Helpers
--------------------------------------------- */
function safeJsonParse(raw) {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function formatNumber(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  return Math.round(x).toLocaleString("en-US");
}

function formatMoneyCompact(n, symbol) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  const abs = Math.abs(x);
  if (abs >= 1e12) return `${symbol}${(x / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${symbol}${(x / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${symbol}${(x / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${symbol}${(x / 1e3).toFixed(2)}K`;
  return `${symbol}${Math.round(x).toLocaleString("en-US")}`;
}

function scenarioLabel(key) {
  switch (key) {
    case "UK":
      return "United Kingdom";
    case "EU":
      return "European Union";
    case "US_FED":
      return "US (Federal)";
    case "US_STATE":
      return "US (State-by-State)";
    default:
      return key;
  }
}

function currencyFromSummary(summary) {
  return {
    symbol: summary?.currency_symbol || "$",
    code: summary?.currency_code || "USD",
  };
}

function toNum(x, fb = 0) {
  const n = Number(x);
  return Number.isFinite(n) ? n : fb;
}

/**
 * Normalize payload into a stable "v2-like" shape for rendering.
 * B-mode: We do NOT read v3 engine blobs here. We only use:
 * - payload.v2 if present (preferred)
 * - payload.summary + payload.scenarios as the canonical adapter output
 */
function normalizeForRender(payload) {
  const summary = payload?.summary || {};
  const scenariosObj = payload?.scenarios && typeof payload.scenarios === "object" ? payload.scenarios : {};

  // Build per-regime tiles from scenarios (canonical)
  const per_regime = Object.keys(scenariosObj)
    .filter((k) => ["US_FED", "US_STATE", "EU", "UK"].includes(k))
    .map((k) => {
      const s = scenariosObj[k] || {};
      return {
        scenario_key: k,
        display_name: s.display_name || scenarioLabel(k),
        jurisdiction_id: s.jurisdiction_id || null,
        currency_code: s.currency_code || (k === "EU" ? "EUR" : k === "UK" ? "GBP" : "USD"),
        currency_symbol: s.currency_symbol || (k === "EU" ? "€" : k === "UK" ? "£" : "$"),
        exposure_ceiling_native: toNum(s.exposure_ceiling_native, 0),
        statutory_base_fine_native:
          typeof s.statutory_base_fine_native === "number" ? s.statutory_base_fine_native : null,
        enabled: !!s.enabled,
      };
    })
    .filter((x) => x.enabled);

  // Headline (canonical)
  const cur = currencyFromSummary(summary);
  const worst = toNum(summary?.headline_exposure_native, 0);

  // Floor: prefer summary, else deterministic MVP 65% guardrail
  const floor = Number.isFinite(Number(summary?.floor_native))
    ? toNum(summary?.floor_native, 0)
    : Math.round(worst * 0.65);

  // Net: prefer summary, else deterministic MVP 12% credit guardrailed to floor
  const net = Number.isFinite(Number(summary?.net_native))
    ? toNum(summary?.net_native, 0)
    : Math.round(Math.max(floor, Math.min(worst, worst * 0.88)));

  // Ceiling: prefer summary, else conservative visible cap heuristic
  let ceiling = toNum(summary?.statutory_ceiling_visible_native, NaN);
  if (!Number.isFinite(ceiling)) {
    const base = toNum(summary?.statutory_base_fine_usd, 0); // legacy-key but still canonical summary slot in your adapter
    const cap = base > 0 ? base * 10 : 0;
    ceiling = Math.round(Math.max(worst, cap));
  }

  // Disclaimer (canonical if present; minimal fallbacks to avoid blank banner)
  const d = payload?.disclaimer && typeof payload.disclaimer === "object" ? payload.disclaimer : null;
  const disclaimer = {
    acknowledged:
      !!d?.acknowledged ||
      !!payload?.disclaimer_acknowledged ||
      !!payload?.inputs?.disclaimer_acknowledged,
    text_version:
      d?.text_version ||
      payload?.disclaimer_text_version ||
      payload?.inputs?.disclaimer_text_version ||
      null,
  };

  // Preferred: payload.v2 if already built elsewhere
  const v2 = payload?.v2 && typeof payload.v2 === "object"
    ? payload.v2
    : {
        structured_worst_case: {
          exposure_native: worst,
          currency: { code: cur.code, symbol: cur.symbol },
        },
        floor: { exposure_native: floor },
        statutory_ceiling: { exposure_native: ceiling },
        net: { exposure_native: net },
        combined_stacked: { per_regime },
      };

  return { summary, scenariosObj, v2, disclaimer, cur };
}

/* ---------------------------------------------
   Mini stacked tile row (no FX)
--------------------------------------------- */
function CombinedStacked({ perRegime }) {
  const items = Array.isArray(perRegime) ? perRegime : [];
  if (!items.length) return null;

  // For bar width, compare within same currency only
  const maxByCurrency = items.reduce((acc, it) => {
    const c = it.currency_code || "USD";
    const v = Number(it.exposure_ceiling_native) || 0;
    acc[c] = Math.max(acc[c] || 0, v);
    return acc;
  }, {});

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
            Combined Stacked Exposure
          </div>
          <div className="mt-2 text-sm text-slate-700">
            Side-by-side regime exposure tiles (native currency). Not FX-converted.
          </div>
        </div>
        <div className="text-[11px] text-slate-500">Comparative within currency</div>
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((it) => {
          const key = it.scenario_key || it.jurisdiction_id || it.display_name || "REGIME";
          const code = it.currency_code || "USD";
          const sym = it.currency_symbol || "$";
          const val = Number(it.exposure_ceiling_native) || 0;

          const denom = Math.max(1, Number(maxByCurrency[code]) || 1);
          const pct = Math.max(0, Math.min(1, val / denom));

          return (
            <div key={key} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <div className="flex items-center justify-between">
                <div className="text-sm font-black text-slate-900">
                  {it.display_name || scenarioLabel(it.scenario_key) || "Regime"}
                </div>
                <div className="text-xs font-black text-slate-600">{code}</div>
              </div>

              <div className="mt-2 text-3xl font-black text-slate-900">
                {formatMoneyCompact(val, sym)}
              </div>

              <div className="mt-3 h-2 w-full rounded-full bg-white">
                <div
                  className="h-2 rounded-full bg-slate-900"
                  style={{ width: `${(pct * 100).toFixed(2)}%` }}
                />
              </div>

              <div className="mt-2 text-xs text-slate-500">
                Shown in native currency (no FX conversion).
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------------------------------------
   Page
--------------------------------------------- */
export default function UniversalRiskAuditResults() {
  const navigate = useNavigate();

  const payload = useMemo(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    return safeJsonParse(raw);
  }, []);

  // Missing payload → graceful fallback
  if (!payload) {
    return (
      <div className="min-h-screen bg-white">
        <header className="border-b border-slate-200 bg-white">
          <div className="mx-auto max-w-6xl px-5 py-7">
            <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
              AI Fine Protector
            </div>
            <h1 className="mt-2 text-2xl font-black text-slate-900">
              Universal Risk Audit Results
            </h1>
            <div className="mt-2 text-sm text-slate-600">
              No stored results found. Please run the audit again.
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <button
                onClick={() => navigate("/universal-audit")}
                className="rounded-2xl bg-slate-900 px-5 py-2.5 text-sm font-black text-white hover:bg-slate-800"
              >
                Run Audit
              </button>
              <button
                onClick={() => navigate("/dashboard")}
                className="rounded-2xl border border-slate-300 bg-white px-5 py-2.5 text-sm font-black text-slate-700 hover:border-slate-400"
              >
                Back to Dashboard
              </button>
            </div>

            <div className="mt-10 text-xs text-slate-400">
              © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
            </div>
          </div>
        </header>
      </div>
    );
  }

  const inputs = payload?.inputs || {};
  const generatedAt = payload?.generated_at || "—";

  const industry = inputs?.industry || "—";
  const aiUseCase = inputs?.ai_use_case || "—";
  const humans = inputs?.humans_impacted ?? null;
  const revenue = inputs?.annual_revenue_estimate ?? null;
  const juris = Array.isArray(inputs?.jurisdictions_selected) ? inputs.jurisdictions_selected : [];

  const { v2, disclaimer, cur } = normalizeForRender(payload);

  const headlineWorst = toNum(v2?.structured_worst_case?.exposure_native, 0);
  const floor = toNum(v2?.floor?.exposure_native, 0);
  const ceiling = toNum(v2?.statutory_ceiling?.exposure_native, 0);
  const net = toNum(v2?.net?.exposure_native, 0);

  const perRegime = v2?.combined_stacked?.per_regime || [];

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-5 py-7">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
                AI Fine Protector
              </div>
              <h1 className="mt-2 text-2xl font-black text-slate-900">
                Universal Risk Audit Results
              </h1>
              <div className="mt-2 text-sm text-slate-600">
                Board-ready exposure preview. Values shown in native jurisdiction currency (no FX conversion in this MVP).
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => navigate("/dashboard")}
                className="rounded-2xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-black text-slate-700 hover:border-slate-400"
              >
                Back to Dashboard
              </button>
              <button
                onClick={() => navigate("/universal-audit")}
                className="rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-black text-white hover:bg-slate-800"
              >
                Run Another Audit
              </button>
            </div>
          </div>

          {/* Not Legal Advice banner */}
          <div className="mt-6 rounded-3xl border border-amber-200 bg-amber-50 p-5">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="text-[11px] font-black uppercase tracking-[0.35em] text-amber-900">
                  Not Legal Advice
                </div>
                <div className="mt-1 text-sm text-amber-950">
                  This free Universal Risk Audit is an{" "}
                  <span className="font-black">AI-driven estimate</span> of potential fine exposure
                  based on your inputs.
                </div>
              </div>

              <div className="text-xs text-amber-900">
                {disclaimer?.acknowledged ? (
                  <span className="font-black">Acknowledged</span>
                ) : (
                  <span className="font-black">Acknowledgment not found</span>
                )}
                {disclaimer?.text_version ? (
                  <span className="ml-2 text-[11px] text-amber-800">({disclaimer.text_version})</span>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10 pb-16 space-y-6">
        {/* HERO: Structured Worst Case */}
        <div className="rounded-3xl border border-slate-200 bg-white p-8">
          <div className="text-center">
            <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
              Structured Worst Case (Headline)
            </div>
            <div className="mt-4 text-6xl font-black tracking-tight text-slate-900">
              {formatMoneyCompact(headlineWorst, cur.symbol)}
            </div>
            <div className="mt-2 text-sm text-slate-600">
              Headline currency: <span className="font-black">{cur.code}</span>
            </div>
          </div>

          <div className="mt-7 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-500">
                Floor
              </div>
              <div className="mt-2 text-2xl font-black text-slate-900">
                {formatMoneyCompact(floor, cur.symbol)}
              </div>
              <div className="mt-1 text-xs text-slate-500">Conservative lower bound</div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-500">
                Statutory Ceiling
              </div>
              <div className="mt-2 text-2xl font-black text-slate-900">
                {formatMoneyCompact(ceiling, cur.symbol)}
              </div>
              <div className="mt-1 text-xs text-slate-500">Visible cap (not the hero)</div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-500">
                Net
              </div>
              <div className="mt-2 text-2xl font-black text-slate-900">
                {net ? formatMoneyCompact(net, cur.symbol) : "—"}
              </div>
              <div className="mt-1 text-xs text-slate-500">Guardrailed offsets (preview)</div>
            </div>
          </div>
        </div>

        {/* Combined stacked (mandatory visual; still no FX) */}
        <CombinedStacked perRegime={perRegime} />

        {/* CTA block */}
        <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-7">
          <div className="text-[11px] font-black uppercase tracking-[0.35em] text-emerald-900">
            Go deeper (paid assessment)
          </div>
          <div className="mt-2 text-sm text-emerald-950">
            The paid assessment identifies the drivers of exposure and generates a remediation roadmap designed for governance decisions.
          </div>

          <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-2 text-sm text-emerald-950">
            <li className="flex gap-2">
              <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-emerald-700" />
              <span>Defensible scoring across regimes</span>
            </li>
            <li className="flex gap-2">
              <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-emerald-700" />
              <span>Traceable legal methodology (Legal Mode)</span>
            </li>
            <li className="flex gap-2">
              <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-emerald-700" />
              <span>Scenario modeling (“what-if” planning)</span>
            </li>
            <li className="flex gap-2">
              <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-emerald-700" />
              <span>Downloadable reports & board materials</span>
            </li>
          </ul>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={() => navigate("/pricing")}
              className="rounded-2xl bg-emerald-800 px-6 py-3 text-sm font-black text-white hover:bg-emerald-900"
            >
              View Plans
            </button>

            <button
              type="button"
              onClick={() => navigate("/pricing")}
              className="rounded-2xl border border-emerald-300 bg-white px-6 py-3 text-sm font-black text-emerald-900 hover:bg-emerald-50"
            >
              Preview Advanced Features
            </button>

            <button
              type="button"
              onClick={() => navigate("/universal-audit")}
              className="rounded-2xl border border-emerald-300 bg-white px-6 py-3 text-sm font-black text-emerald-900 hover:bg-emerald-50"
            >
              Run Another Audit
            </button>
          </div>
        </div>

        {/* Input summary */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-3xl border border-slate-200 bg-white p-6">
            <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
              Input Summary
            </div>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs text-slate-500 font-bold">Industry</div>
                <div className="mt-2 text-sm font-black text-slate-900">{industry}</div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs text-slate-500 font-bold">People impacted / month</div>
                <div className="mt-2 text-sm font-black text-slate-900">{formatNumber(humans)}</div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs text-slate-500 font-bold">Annual revenue estimate</div>
                <div className="mt-2 text-sm font-black text-slate-900">
                  {revenue == null ? "—" : `${cur.symbol}${Number(revenue).toLocaleString("en-US")}`}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs text-slate-500 font-bold">Jurisdictions</div>
                <div className="mt-2 text-sm font-black text-slate-900">
                  {juris.length ? juris.join(", ") : "—"}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:col-span-2">
                <div className="text-xs text-slate-500 font-bold">Generated at</div>
                <div className="mt-2 text-sm font-black text-slate-900">
                  <span className="font-mono">{generatedAt}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6">
            <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
              AI Use Case
            </div>
            <div className="mt-4 text-sm text-slate-800 whitespace-pre-wrap">
              {aiUseCase}
            </div>
          </div>
        </div>

        {/* Currency disclaimer */}
        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6">
          <div className="text-xs font-black text-slate-700">Currency & Conversion</div>
          <div className="mt-2 text-sm text-slate-700">
            Values are shown in <span className="font-black">native jurisdiction currency</span> (EU=EUR, UK=GBP, US=USD).
            FX conversion is not applied in this MVP.
          </div>
        </div>

        <div className="pt-6 text-xs text-slate-400">
          © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
        </div>
      </main>
    </div>
  );
}